import { cn } from '@/lib/cn';
import {
  trackRemoteIntroPromoClicked,
  trackRemoteIntroPromoDismissed,
  trackRemoteIntroPromoViewed,
} from '@/models/analytics/features/trackRemoteIntroPromoAnalytics';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyPBold } from '@heidi/design-system/typography';
import { useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import Image from 'next/image';
import { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useReverseTrial } from '@heidi/commerce/data';
import {
  remoteIntroPromoModalEntrySurfaceAtom,
  remoteIntroPromoModalOpenAtom,
} from '../atoms/remote-intro-promo-atoms';
import { useRemoteIntroPromoEligibility } from '../eligibility/useIsRemoteIntroPromoEligible';
import { remoteIntroPromoMessages, remoteIntroPromoSharedMessages } from '../messages';
import { useRemoteIntroPromoSeen } from '../useRemoteIntroPromoSeen';

const SURFACE = 'billing_banner' as const;

interface RemoteIntroPromoBannerProps {
  className?: string;
}

/**
 * Billing-page banner for the Heidi Remote intro promo. Cohort-aware copy,
 * hidden for subscriber cohorts while in any trial. Seen-state lives on the
 * backend via `ModalId.REMOTE_INTRO_PROMO` — dismissing the banner also hides
 * the menu card and survives across devices. Layout matches Figma node
 * 11164-15438: sunlight-yellow card with the product photo + pattern on the
 * left, title + green "Offer" badge + description in the middle, dark "See
 * offer" button + close X on the right.
 */
export const RemoteIntroPromoBanner = ({ className }: RemoteIntroPromoBannerProps) => {
  const intl = useIntl();
  const cohort = useRemoteIntroPromoEligibility();
  const { isInReverseTrial, isFakeClinicianTrial, isEvidenceInFreeTrial } =
    useReverseTrial();
  const { isSeen, markDismissed } = useRemoteIntroPromoSeen();

  const setModalOpen = useSetAtom(remoteIntroPromoModalOpenAtom);
  const setEntrySurface = useSetAtom(remoteIntroPromoModalEntrySurfaceAtom);
  // Tracks the last cohort we emitted `viewed` for — so a cohort transition
  // (rare but possible: trial expires, settings change) emits a fresh
  // impression instead of swallowing it as "already tracked this mount".
  const lastTrackedCohortRef = useRef<typeof cohort>(null);

  const isSubscriberCohort =
    cohort === 'monthly_subscriber' || cohort === 'yearly_subscriber';

  const visible =
    cohort !== null &&
    !isSeen &&
    (!isSubscriberCohort ||
      (!isInReverseTrial && !isFakeClinicianTrial && !isEvidenceInFreeTrial));

  useEffect(() => {
    if (visible && cohort && lastTrackedCohortRef.current !== cohort) {
      lastTrackedCohortRef.current = cohort;
      trackRemoteIntroPromoViewed(SURFACE, cohort);
    }
  }, [visible, cohort]);

  const handleCtaClick = useCallback(() => {
    if (!cohort) return;
    trackRemoteIntroPromoClicked(SURFACE, cohort);
    setEntrySurface(SURFACE);
    setModalOpen(true);
  }, [cohort, setEntrySurface, setModalOpen]);

  const handleDismiss = useCallback(() => {
    if (!cohort) return;
    trackRemoteIntroPromoDismissed(SURFACE, cohort);
    markDismissed();
  }, [cohort, markDismissed]);

  const messages = cohort ? remoteIntroPromoMessages[cohort] : null;

  return (
    <AnimatePresence>
      {visible && messages && (
        <m.div
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: 8, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="overflow-hidden"
          data-testid="remote-intro-promo-banner"
          data-cohort={cohort ?? undefined}
        >
          <div
            className={cn(
              // theme-agnostic: marketing card uses the exact `sunlight/50`
              // (#FEFDE8) tone + Pro-tier violet border specified in Figma
              // 11164-15438. Both surfaces are intentionally fixed across
              // themes — a brand visual, not a UI-state colour.
              'flex h-18 items-center gap-4 overflow-hidden rounded-md border border-tier-pro-border bg-sunlight-50 pe-4',
              className
            )}
          >
            <BannerHero />
            <div className="flex flex-1 items-center justify-between gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <TypographyPBold
                    className="truncate text-text-primary"
                    data-testid="remote-intro-promo-banner-title"
                  >
                    <FormattedMessage {...messages.bannerTitle} />
                  </TypographyPBold>
                  <Badge color="green" size="sm" className="shrink-0 rounded-md">
                    <FormattedMessage
                      defaultMessage="Offer"
                      id="HLat5KqEci"
                      description="Green inline badge shown next to the banner / menu-card title"
                    />
                  </Badge>
                </div>
                <TypographyCaption className="text-text-secondary">
                  <FormattedMessage {...messages.bannerDescription} />
                </TypographyCaption>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleCtaClick}
                  data-testid="remote-intro-promo-banner-cta"
                >
                  <FormattedMessage
                    defaultMessage="View offer"
                    id="bHyejFzelC"
                    description="Primary CTA label on the Billing-page banner (Figma 11164-13875)"
                  />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={handleDismiss}
                  aria-label={intl.formatMessage(
                    remoteIntroPromoSharedMessages.dismissAriaLabel
                  )}
                  data-testid="remote-intro-promo-banner-close"
                  className="text-text-secondary hover:bg-transparent hover:text-text-primary"
                >
                  <X />
                </Button>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Hero area at the left edge — single pre-composed asset (the product photo
 * already laid onto the yellow swirl pattern). 459 × 216 source, scaled to
 * the banner's 72px height; width auto-fits to ~153px to preserve aspect.
 */
const BannerHero = () => (
  <Image
    src="/images/remote-promo/banner-hero.png"
    alt=""
    aria-hidden
    width={459}
    height={216}
    sizes="153px"
    priority={false}
    className="h-full w-auto shrink-0"
  />
);
